/* eslint-disable max-lines */
import { SOFTWARE_QUALITY } from '@/lib/constants/appsec/sast'

export const APPSEC = {
  MUTE: {
    MUTE_BUTTON: 'Mute finding',
    UNMUTE_BUTTON: 'Unmute finding',
    DIALOG_TITLE: 'Reason for mute',
    SELECTION_SUMMARY: (selected: number, total: number) => `${selected} of ${total} findings selected`,
    JUSTIFICATION_LABEL: 'Justification',
    JUSTIFICATION_PLACEHOLDER:
      'Why is this finding being muted? e.g. a false positive in generated code, accepted until the next release',
    CANCEL: 'Cancel',
    BANNER_TITLE: (muted: number, total: number) => `Muted · ${muted} of ${total} findings`,
    MUTED_BADGE: 'Muted',
    MUTED_BY: (name: string) => `Muted by ${name}`,
    // Not "the cloud scanner": these come from a code scanner. It also covers a mute applied before
    // the author column existed, which is what the rows muted by hand are.
    BY_SCANNER: 'Muted by the scanner',
    NOTHING_SELECTED_TOOLTIP: 'Select at least one finding',
    DORMANT_NOTE: 'This mute will apply again if the finding is reported in a future scan',
    MUTE_SUCCESS: 'Finding muted',
    UNMUTE_SUCCESS: 'Finding unmuted',
    MUTE_PARTIAL: (muted: number, requested: number) =>
      `Muted ${muted} of ${requested} findings — the rest are no longer open`,
    UNMUTE_PARTIAL: (unmuted: number, requested: number) =>
      `Unmuted ${unmuted} of ${requested} findings — the rest were no longer muted`,
    MUTE_ERROR: 'Could not mute the finding',
    UNMUTE_ERROR: 'Could not unmute the finding',
  },
  COMMON: {
    CVSS_SCORE: 'CVSS Score',
    CWE: 'CWE',
    CVE: 'CVE',
    TIME_TO_RESOLVE: {
      RESOLVED: 'Resolved',
      RESOLVED_IN: (duration: string) => `Resolved in ${duration}`,
      FIRST_SEEN: 'First seen',
      CLOSED: 'Closed',
      DURATION: 'Time to resolve',
      DAYS: (days: number) => (days === 1 ? '1 day' : `${days} days`),
      LESS_THAN_A_DAY: 'less than a day',
    },
    ITEM: {
      DEPENDENCIES: 'dependencies',
      CONTAINERS: 'containers',
      SERVICES: 'services',
      REPOSITORIES: 'repositories',
      FILES: 'files',
    },
    DETAILS: {
      DESCRIPTION: 'Description',
      REMEDIATION: 'Remediation',
      SUMMARY: 'Summary',
      PACKAGES: 'Packages',
    },
    EXPORT: {
      BUTTON: 'Export CSV',
      EMPTY: 'There are no findings to export.',
      STATUS: 'Status',
      AUTHOR: 'Author',
      MUTED_REASON: 'Mute reason',
      MUTED_BY: 'Muted by',
      MUTED_WHEN: 'Muted on',
    },
  },
  DASHBOARD: {
    TITLE: 'Application Security Dashboard',
    GENERAL_METRICS: {
      IMAGES: 'Scanned Containers',
      WEBAPPS: 'Scanned Webapps',
      REPOSITORIES: 'Scanned Repositories',
    },
    TOP_FIVE: {
      CONTAINERS: 'Top Risk Containers',
      WEBAPPS: 'Top Risk Web Apps',
      REPOSITORIES: 'Top Risk Repositories',
      RISK_SCORE: 'Risk score',
      NO_DATA: 'No data available',
    },
    FINDINGS_OVERVIEW: {
      TOTAL_FINDINGS: 'Current Findings',
      CRITICAL: 'Critical',
      HIGH: 'High',
      MEDIUM: 'Medium',
      LOW: 'Low',
      TITLE: 'Findings Overview',
      NO_DATA: 'No data available',
    },
    TRENDS: {
      sast: 'SAST',
      sca: 'SCA',
      secrets: 'Secrets',
      dast: 'DAST',
      containers: 'Containers',
    },
    TRENDS_TITLE: (trend: string) => `${trend} Trends`,
    TRENDS_COMPUTING: 'Computing trends',
    FINDINGS_BY_TYPE: 'Findings by Type',
    LAST_SCAN: 'Last scan',
  },
  SAST: {
    TITLE: 'Static Application Security Testing',
    SUBTITLE: 'Security issues detected from static code analysis',
    OVERVIEW: {
      BY_TYPE: 'Type',
    },
    SOFTWARE_QUALITY: {
      [SOFTWARE_QUALITY.SECURITY]: 'Security',
      [SOFTWARE_QUALITY.RELIABILITY]: 'Reliability',
      [SOFTWARE_QUALITY.MAINTAINABILITY]: 'Maintainability',
      [SOFTWARE_QUALITY.READABILITY]: 'Readability',
    },
    TABLE: {
      NAME: 'Name',
      SOFTWARE_QUALITY: 'Type',
      ISSUES: 'Issues',
      SEVERITY: 'Severity',
      FILES: 'Files',
      REPOSITORY: 'Repository',
      REPOSITORIES: 'Repositories',
    },
    STATUS: {
      OPEN: 'Failed',
      CLOSED: 'Passed',
      MUTED: 'Muted',
    },
    DETAILS: {
      ISSUES: 'Issues',
      DESCRIPTION: 'Description',
      REMEDIATION: 'Remediation',
      AFFECTED_FILES: 'Affected Files',
      AUTHOR: 'Author:',
      FILE_PATH: 'File Path',
      LINE: 'Line',
      CODE_SNIPPET: 'Code Snippet',
      NEW: 'New',
      REPOSITORIES: 'Repositories',
    },
    PAGINATION: {
      SHOWING: 'Showing',
      OF: 'of',
      PREVIOUS: 'Previous',
      NEXT: 'Next',
    },
  },
  SCA: {
    TITLE: 'Software Composition Analysis',
    SUBTITLE: 'Vulnerabilities detected in third-party libraries and dependencies',
    TABS: {
      VULNERABILITIES: 'Vulnerabilities',
      DEPENDENCIES: 'SBOM Inventory',
    },
    OVERVIEW: {
      SEVERITY: 'Severity',
      NO_ISSUES: 'No issues',
      TOP_PACKAGES: 'Top Packages',
    },
    TABLE: {
      CVE: 'CVE',
      VULNERABILITY_DESCRIPTION: 'Vulnerability Description',
      SEVERITY: 'Severity',
      PACKAGE_NAME: 'Package',
      PACKAGE_VERSION: 'Version',
      REPOSITORY: 'Repository',
      STATUS: 'Status',
      REPOSITORIES: 'Repositories',
    },
    STATUS: {
      OPEN: 'Failed',
      CLOSED: 'Passed',
      MUTED: 'Muted',
    },
    DETAILS: {
      AFFECTED_PACKAGES: 'Affected packages',
      DESCRIPTION: 'Description',
      REMEDIATION: 'Remediation',
    },
  },
  SECRETS_DETECTION: {
    TITLE: 'Secrets Detection',
    SUBTITLE: 'Exposed credentials and API keys detected in code',
    TABLE: {
      SECRET_CATEGORY: 'Secret Category',
      SEVERITY: 'Severity',
      FILE_PATH: 'File',
      LINE_NUMBER: 'Line',
      SECRET_MATCH: 'Match',
      REPOSITORY_NAME: 'Repository',
      REPOSITORIES: 'Repositories',
      STATUS: 'Status',
    },
    STATUS: {
      OPEN: 'Failed',
      CLOSED: 'Passed',
      MUTED: 'Muted',
    },
  },
  SBOM: {
    TITLE: 'Software Bill of Materials',
    SUBTITLE: 'Complete inventory of all software components and dependencies',
    SEARCH_PLACEHOLDER: 'Search dependencies...',
    TABLE: {
      DEPENDENCY_NAME: 'Dependency Name',
      VERSION: 'Version',
      LICENSE: 'License',
      TYPE: 'Type',
    },
  },
  CONTAINERS_SCAN: {
    TITLE: 'Container Security',
    SUBTITLE: 'Security issues detected in container images',
    TABLE: {
      VULNERABILITY_NAME: 'Vulnerability Name',
      SEVERITY: 'Severity',
      PACKAGE: 'Package',
      PACKAGE_AND_VERSION: 'Package:Version',
      CONTAINER: 'Container',
      STATUS: 'Status',
      VERSION: 'Version',
      CONTAINERS: 'Containers',
    },
    STATUS: {
      OPEN: 'Failed',
      CLOSED: 'Passed',
      MUTED: 'Muted',
    },
    DETAILS: {
      DESCRIPTION: 'Description',
      REMEDIATION: 'Remediation',
      CVSS_SCORE: 'CVSS Score',
      CVE: 'CVE',
      REFERENCES: 'References',
      AFFECTED_CONTAINERS: 'Affected Containers',
    },
  },
  DAST: {
    TITLE: 'Dynamic Application Security Testing',
    SUBTITLE: 'Security issues detected from live website scans',
    OVERVIEW: {
      SEVERITY: 'Severity',
      NO_ISSUES: 'No issues',
      VULNERABILITIES: 'Vulnerabilities',
      TOP_SUBDOMAINS: 'Top Subdomains',
    },
    TABLE: {
      VULNERABILITY_NAME: 'Name',
      SEVERITY: 'Severity',
      AFFECTED_SERVICES: 'Services',
      SUBDOMAIN: 'Subdomain',
      SUBDOMAINS: 'Subdomains',
      STATUS: 'Status',
    },
    STATUS: {
      OPEN: 'Failed',
      CLOSED: 'Passed',
      MUTED: 'Muted',
    },
    DETAILS: {
      DESCRIPTION: 'Description',
      REMEDIATION: 'Remediation',
      CVSS_SCORE: 'CVSS Score',
      CWE: 'CWE',
      REFERENCES: 'References',
      AFFECTED_SERVICES: 'Affected Services',
      SUBDOMAINS: 'Subdomains',
      HOST: 'Host',
      PATH: 'Path',
    },
    PAGINATION: {
      SHOWING: 'Showing',
      OF: 'of',
      PREVIOUS: 'Previous',
      NEXT: 'Next',
    },
  },
} as const
